import { Alert, AlertDescription, Button } from '@design-system';
import { durationInWords, isHybridConsumerQuote, paymentScheduleOfQuote } from '@shared/consumerPlan';
import { CoverageSplit, DetailList, MaxBenefitsAtAge } from '../components/figures';
import { PolicyReviewInProgressNotice } from '../components/policy-review-in-progress-notice';
import { SelectedCoverageSummary } from '../components/selected-coverage-summary';
import { StepFrame } from '../components/step-frame';
import { useConsumerFlow } from '../hooks/use-consumer-flow';
import { usePlanTotals } from '../hooks/use-plan-totals';
import { GUARANTEE_CONDITIONS } from '../lib/copy';
import { money } from '../lib/format';
import type { ConsumerStepProps } from './step-props';

const SCHEDULE_HELP: Record<ConsumerPaymentSchedule, string> = {
  tenPay: 'You pay for 10 years. Coverage starts when the policy takes effect and continues afterward under its terms.',
  singlePay: 'You make one premium payment when the policy takes effect.',
  ongoing: 'Ongoing premiums may change.',
};

export function CoverageStep({ clientId }: ConsumerStepProps) {
  const { goTo, back } = useConsumerFlow(clientId);
  const { selectedQuote, selectedProjection, scenario, summaryStep } = usePlanTotals(clientId);
  if (!selectedQuote || !selectedProjection || !scenario) return null;

  const paysBeneficiaries = isHybridConsumerQuote(selectedQuote) && selectedProjection.deathBenefitWithoutCare > 0;
  const schedule = paymentScheduleOfQuote(selectedQuote) ?? 'ongoing';

  return (
    <StepFrame
      step="coverage"
      title="Does this coverage work for you?"
      subtitle={`Scenario: care around age ${scenario.careStartAge} · about ${durationInWords(scenario.totalMonths)}`}
      onBack={() => back('options')}
      help={
        <>
          <p>
            The <strong>projected payment</strong> applies your care scenario to this policy. The{' '}
            <strong>maximum</strong> is a limit, not a promise that all of it will be used.
          </p>
          <p>{SCHEDULE_HELP[schedule]}</p>
          <div className="flex flex-col items-start">
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('policy-terms', { returnTo: 'coverage' })}>
              See policy terms
            </Button>
            <Button variant="link" className="px-0 has-[>svg]:px-0" onClick={() => goTo('breakdown', { returnTo: 'coverage' })}>
              See the care cost breakdown
            </Button>
          </div>
        </>
      }
      primary={{ label: 'Use this coverage', onClick: () => goTo(summaryStep) }}
      secondary={{ label: 'Choose another option', onClick: () => back('options') }}
      next="see how the whole care event comes together, then apply."
    >
      <PolicyReviewInProgressNotice clientId={clientId} />
      <SelectedCoverageSummary quote={selectedQuote} />
      <CoverageSplit
        projectedPayout={selectedProjection.projectedPayout}
        outOfPocket={selectedProjection.outOfPocket}
      >
        <MaxBenefitsAtAge
          age={scenario.careStartAge}
          monthlyMax={selectedProjection.monthlyMaxAtCareStart}
          totalMax={selectedProjection.maxBenefitAtCareStart}
        />
      </CoverageSplit>

      <DetailList
        rows={[
          ...(paysBeneficiaries
            ? [
                {
                  label: 'For beneficiaries after this care event',
                  value: `at least ${money(selectedProjection.guaranteedDeathBenefitAfterCare)}`,
                },
                {
                  label: 'For beneficiaries if you never need care',
                  value: money(selectedProjection.deathBenefitWithoutCare),
                },
              ]
            : [{ label: 'Benefit for beneficiaries', value: 'None' }]),
        ]}
      />
      <p className="text-muted-foreground text-xs">
        {paysBeneficiaries && `Beneficiary amounts are alternative outcomes, not added together. ${GUARANTEE_CONDITIONS} `}
        Projected care payments are estimates.
      </p>

      <Alert variant="info">
        <AlertDescription>
          <strong>Coming up:</strong> how this policy pays across the whole care event and what you’d cover
          yourself, with the option to apply right away.
        </AlertDescription>
      </Alert>
    </StepFrame>
  );
}
